import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import api from '../../http';
import { authLog, authStorage, LoginCredentials } from '@practium/features/auth';
import { User } from '@practium/shared';
import { getApiErrorMessage, toApiError, getApiUrl } from '../utils';
import { createConsentError, isConsentRequired } from './consentCheck';
import { isAnonymizationPending, toAnonymizationPendingError } from './anonymizationCheck';

interface ApiResponseLogin {
	sessionId: string;
	csrfToken: string;
	accessToken: string;
}

export const useLogin = () => {
	return useMutation({
		mutationFn: async (credentials: LoginCredentials) => {
			const response = await api.post<ApiResponseLogin>('/login', credentials).catch((error) => {
				const anonymizationError = toAnonymizationPendingError(error);
				if (anonymizationError) throw anonymizationError;
				throw toApiError(error, 'Erro ao fazer login');
			});

			const cleanToken = response.data.accessToken.replace(/^bearer\s+/i, '');
			const { csrfToken } = response.data;

			const { data: user } = await axios
				.get<User>(`${getApiUrl()}user/get`, {
					headers: { Authorization: `bearer ${cleanToken}` },
				})
				.catch((error) => {
					if (isConsentRequired(error)) throw error;
					if (axios.isAxiosError(error) && error.response?.status === 428) {
						throw createConsentError(cleanToken);
					}
					throw toApiError(error, 'Erro ao fazer login');
				});

			return { user, token: cleanToken, csrfToken, rememberMe: credentials.rememberMe ?? false };
		},
		onSuccess: ({ user, token, csrfToken, rememberMe }) => {
			authStorage.saveSession(user, token, rememberMe);
			authStorage.saveCsrf(csrfToken);
			authLog.logLogin(user._id, user.email, rememberMe || false);
		},
		onError: (error) => {
			if (isConsentRequired(error) || isAnonymizationPending(error)) return;
			console.error('Erro ao fazer login:', getApiErrorMessage(error, 'Erro ao fazer login'));
		},
	});
};
